import { useNavigate } from 'react-router-dom';
import { getName } from 'i18n-iso-countries';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo } from 'react';
import { Graph } from '../../components/Graph';
import { withLoadingAndOverlay } from '../../hoc';

function GeoKpiScore({ data: countryKpiScoreData = [] }) {
  const countryKpiScore = useMemo(() => {
    if (countryKpiScoreData?.length) {
      const updatedArray = countryKpiScoreData.map((obj) => {
        const countryKpiScoreObj = {};
        countryKpiScoreObj.x = obj.x ? getName(obj.x, 'en') : obj.x;
        countryKpiScoreObj.xCode = obj.x;
        countryKpiScoreObj.y = obj.y;
        countryKpiScoreObj.r = obj.r;
        countryKpiScoreObj.accounts = obj.accounts;
        return countryKpiScoreObj;
      });
      return updatedArray;
    }
    return [];
  }, [countryKpiScoreData]);
  const navigate = useNavigate();

  const labels = useMemo(() => {
    const array = [''];
    countryKpiScore.forEach((obj) => {
      array.push(obj.x);
    });
    return array;
  }, [countryKpiScore]);

  const datasets = [
    {
      data: countryKpiScore,
      backgroundColor: ['#4482FF', '#294E99', '#f3bc00', '#00af82', '#0075a4', '#0075a4'],
      borderColor: ['#4482FF', '#294E99', '#f3bc00', '#00af82', '#0075a4', '#0075a4'],
    },
  ];

  const options = {
    scales: {
      x: {
        beginAtZero: true,
        type: 'category',
        labels,
        title: {
          display: true,
          text: 'Country',
        },
        ticks: {
          autoSkip: false, // Ensure all labels are displayed
          maxRotation: 40, // Maximum rotation for x-axis labels
          minRotation: 40, // Minimum rotation for x-axis labels
        },
      },
      y: {
        ticks: { precision: 0 },
        suggestedMax: 5,
        beginAtZero: true,
        labels: false,
        title: {
          display: true,
          text: 'Average KPI Score',
        },
      },
    },
    onHover: (event, chartElement) => {
      event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
    },
    onClick: (event, chartElement) => {
      const { index } = chartElement.length && chartElement[0];
      if (index || index === 0) {
        const tooltipEl = document.getElementById('chartjs-tooltip');
        tooltipEl.style.opacity = '0';
        const bubbleValue = countryKpiScore && countryKpiScore.length ? countryKpiScore[index] : 0;
        if (bubbleValue && bubbleValue.xCode) {
          navigate('/account-list', { state: { filters: { country: [bubbleValue.xCode], licenceStatus: ['Active'], accountVisibility: true }, showAdvanceFilter: true } });
        }
      }
    },
    responsive: true,
    plugins: {
      legend: false,
      title: {
        display: true,
        text: 'Country vs Average KPI Score',
        padding: 15,
        align: 'center',
        position: 'top',
        font: {
          size: 14,
        },
      },
      datalabels: {
        display: false, // Disable data labels on bubbles
      },
      tooltip: {
        enabled: false,
        mode: 'index',
        external(context) {
          let tooltipEl = document.getElementById('chartjs-tooltip');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.innerHTML = '<table></table>';
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          tooltipEl.classList.remove('below', 'no-transform');
          const { $context = {} } = tooltipModel;
          const { tooltipItems = [] } = $context;
          if (tooltipItems && tooltipItems.length) {
            const label = tooltipItems?.[0]?.raw?.x ?? '';
            const kpiScore = tooltipItems?.[0]?.raw?.y ?? 0;
            const account = tooltipItems?.[0]?.raw?.accounts ?? 0;
            const innerHTML = `
              <div style="width: 150px; background-color: rgba(0, 0, 0, 0.7); border-radius: 5px; box-shadow: 0 0 5px rgba(0, 0, 0, 0.5); padding: 3px;">
                  <div style="padding: 3px; background-color: transparent; border-top-left-radius: 5px; border-top-right-radius: 5px;">
                      <span style="color: white; font-size: 15px;">${label}</span>
                  </div>
                  <hr style="margin: 0; border-color: white;">
                  <div style="padding: 3px;">
                  <p style="color: white; margin-left: 4px; font-size: 14px; display: flex; align-items: center; gap: 5px">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #4482FF;"></span>
                  Account : ${account}
                </p>
                <p style="color: white; margin-left: 4px; font-size: 14px; display: flex; align-items: center; gap: 5px">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: #f28e00;"></span>
                  Avg. KPI Score : ${kpiScore}
                </p>
                  </div>
              </div>`;

            tooltipEl.querySelector('table').innerHTML = innerHTML;
          }

          const position = context.chart.canvas.getBoundingClientRect();

          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';

          const tooltipWidth = tooltipEl.offsetWidth;
          let left = position.left + window.pageXOffset + tooltipModel.caretX;
          if (left + tooltipWidth > position.right) {
            left -= (left + tooltipWidth - position.right);
          }

          tooltipEl.style.left = `${left}px`;

          tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px ${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = 'none';
        },

      },
    },
  };

  return (
    <Graph
      type="bubble"
      labels={labels}
      datasets={datasets}
      options={options}
      plugins={[ChartDataLabels]}
    />
  );
}

export default withLoadingAndOverlay(GeoKpiScore);
